import { useState, useCallback, useMemo } from "react";
import {
  ContextualSaveBar,
  Page,
  Layout,
  BlockStack,
  Text,
  Box,
  Banner,
  Modal,
  Spinner,
  Card,
  InlineStack,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@app/store/store";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { AppPageWrapper } from "@shared/ui";
import { Venue, ImageDetailType, MediaItem, MultilingualText } from "@features/events/types";
import OverviewCard from "./components/OverviewCard";
import LocationCard from "./components/LocationCard";
import MediaCard from "./components/MediaCard";
import { getLocalizedText } from "@shared/lib";

// Removed: Using getLocalizedText from utils instead

export default function VenueDetails() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc, venueId } = useParams<{
    eventOrganizerId: string;
    venueId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { GET, POST, PUT, DELETE } = useApiRequest();
  const queryClient = useQueryClient();
  const selectedLanguages = useSelector(
    (state: RootState) => state.event.selectedLanguages
  );

  const isNewVenue = venueId === "new";

  // State
  const [venueDataDraft, setVenueDataDraft] = useState<Partial<Venue> | null>(() => {
    if (isNewVenue) {
      return {
        name: {},
        description: {},
        slug: "",
        maxCapacity: 0,
        video: "",
        media: [],
      };
    }
    return null;
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(isNewVenue);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch venue data (only for existing venues)
  const { data: venue, isLoading } = useQuery<Venue>({
    queryKey: ["venue", hcc, venueId],
    queryFn: () =>
      GET(`/api/healthcare-companies/${hcc}/e/venues/${venueId}`),
    enabled: Boolean(hcc && venueId && !isNewVenue),
  });

  const initialVenueData = useMemo<Partial<Venue>>(
    () =>
      isNewVenue
        ? {
            name: {},
            description: {},
            slug: "",
            maxCapacity: 0,
            video: "",
            media: [],
          }
        : venue
        ? {
            ...venue,
            name: venue.name || {},
            description: venue.description || {},
            geoAddress: venue.geoAddress,
            media: venue.media || [],
          }
        : {},
    [isNewVenue, venue]
  );
  const venueData = venueDataDraft ?? initialVenueData;

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: Partial<Venue>) => {
      if (isNewVenue) {
        return POST(`/api/healthcare-companies/${hcc}/e/venues`, data);
      } else {
        return PUT(
          `/api/healthcare-companies/${hcc}/e/venues/${venueId}`,
          data
        );
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (data: any) => {
      if (isNewVenue && data?.id) {
        queryClient.invalidateQueries({ queryKey: ["venues", hcc] });
        navigate(`/e/${hcc}/venues/${data.id}`);
      } else {
        queryClient.invalidateQueries({ queryKey: ["venue", hcc, venueId] });
        queryClient.invalidateQueries({ queryKey: ["venues", hcc] });
      }
      setIsDirty(false);
      setFieldErrors({});
    },
    onError: (error: unknown) => {
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: { data?: { errors?: Record<string, string> } };
        };
        if (apiError.response?.data?.errors) {
          setFieldErrors(apiError.response.data.errors);
        }
      }
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () =>
      DELETE(`/api/healthcare-companies/${hcc}/e/venues/${venueId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["venues", hcc] });
      navigate(`/e/${hcc}/venues${location.search}`);
    },
  });


  // Video thumbnail upload mutation
  const { mutateAsync: uploadVideoThumbnail, isPending: isUploadingThumbnail } =
    useMutation({
      mutationFn: async (
        file: File
      ): Promise<ImageDetailType[]> => {
        const formData = new FormData();
        formData.append("file", file);

        if (!hcc || !venueData.id) {
          throw new Error(
            "HCC and Venue ID are required for video thumbnail upload"
          );
        }

        return POST(
          `/api/healthcare-companies/${hcc}/e/venues/${venueData.id}/media`,
          formData
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["venue", hcc, venueId] });
      },
    });

  // Validate multilingual field
  const validateMultilingualField = useCallback(
    (field: MultilingualText | undefined): boolean => {
      if (!field) return false;
      return selectedLanguages.every(
        (lang) => field[lang] && field[lang]!.trim().length > 0
      );
    },
    [selectedLanguages]
  );

  // Handle data changes
  const handleDataChange = useCallback(
    (updates: Partial<Venue>) => {
      const newVenueData = { ...venueData, ...updates };
      setVenueDataDraft(newVenueData);
      setIsDirty(true);

      setFieldErrors((prevErrors) => {
        const newErrors = { ...prevErrors };

        Object.keys(updates).forEach((field) => {
          switch (field) {
            case "name":
              if (validateMultilingualField(updates.name)) {
                delete newErrors.name;
              }
              break;
            case "description":
              if (validateMultilingualField(updates.description)) {
                delete newErrors.description;
              }
              break;
            case "slug":
              if (updates.slug?.trim()) {
                delete newErrors.slug;
              }
              break;
            case "maxCapacity":
              if (updates.maxCapacity && updates.maxCapacity > 0) {
                delete newErrors.maxCapacity;
              }
              break;
            case "geoAddress":
              if (
                (updates.geoAddress?.googlePlaceId && updates.geoAddress.googlePlaceId.trim())
              ) {
                delete newErrors.geoAddress;
              }
              break;
          }
        });

        return newErrors;
      });
    },
    [venueData, validateMultilingualField]
  );

  // Handle save
  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};

    // Validate required fields
    if (!validateMultilingualField(venueData.name)) {
      errors.name = t('venues.details.field.title.validation');
    }
    if (!validateMultilingualField(venueData.description)) {
      errors.description = t('venues.details.field.description.validation');
    }
    if (!venueData.slug?.trim()) {
      errors.slug = t('events.component.slug_picker.slug_required');
    }
    if (!venueData.maxCapacity || venueData.maxCapacity <= 0) {
      errors.maxCapacity = t('venues.details.field.max_capacity.validation');
    }
    console.log('venueData.geoAddress', venueData.geoAddress);
    if (!venueData.geoAddress?.googlePlaceId?.trim()) {
      errors.geoAddress = t('events.component.address_picker.location_required');
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Prepare data for API
    const dataToSave: Partial<Venue> = {
      name: venueData.name,
      description: venueData.description,
      slug: venueData.slug,
      maxCapacity: venueData.maxCapacity,
      geoAddress: venueData.geoAddress,
      video: venueData.video || undefined,
      videoImage: venueData.videoImage || undefined,
      media: venueData.media || [],
    };

    saveMutation.mutate(dataToSave);
  }, [venueData, validateMultilingualField, t, saveMutation]);

  // Handle delete
  const handleDelete = useCallback(() => {
    deleteMutation.mutate();
    setShowDeleteModal(false);
  }, [deleteMutation]);

  // Handle discard changes
  const handleDiscard = useCallback(() => {
    if (isNewVenue) {
      navigate(`/e/${hcc}/venues${location.search}`);
    } else {
      setVenueDataDraft(null);
      setIsDirty(false);
      setFieldErrors({});
    }
  }, [isNewVenue, navigate, hcc, location.search]);

  // Handle media operations
  const handleRemoveMedia = useCallback(
    (id: string) => {
      const updatedMedia = (venueData.media || []).filter(
        (item) => getLocalizedText(item.name) !== id
      );
      handleDataChange({ media: updatedMedia });
    },
    [venueData.media, handleDataChange]
  );

  const handleReorderMedia = useCallback(
    (reorderedItems: MediaItem[]) => {
      handleDataChange({ media: reorderedItems });
    },
    [handleDataChange]
  );

  const handleDropMedia = useCallback(
    (reorderedItems: MediaItem[]) => {
      // This is called when drag ends, could be used for auto-save
      handleDataChange({ media: reorderedItems });
    },
    [handleDataChange]
  );

  // Handle video thumbnail
  const handleVideoThumbnailUpload = useCallback(
    async (files: File[]) => {
      if (files.length > 0 && venueData.id) {
        try {
          const uploadedImages = await uploadVideoThumbnail(files[0]);
          handleDataChange({ videoImage: uploadedImages });
        } catch (error) {
          console.error("Video thumbnail upload failed:", error);
        }
      }
    },
    [venueData.id, uploadVideoThumbnail, handleDataChange]
  );

  const pageTitle = isNewVenue
    ? t('venues.details.new_page_title')
    : getLocalizedText(venueData.name) || t('venues.details.new_page_title');

  if (isLoading) {
    return (
      <AppPageWrapper>
        <Page
          title={t('venues.list.page_title')}
          backAction={{ onAction: () => navigate(`/e/${hcc}/venues${location.search}`) }}
        >
          <Layout>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Box padding="400">
                    <InlineStack align="center">
                      <Spinner size="large" />
                    </InlineStack>
                  </Box>
                </BlockStack>
              </Card>
            </Layout.Section>
          </Layout>
        </Page>
      </AppPageWrapper>
    );
  }

  return (
    <AppPageWrapper>
      {isDirty && (
        <ContextualSaveBar
          backgroundColor="#2B0069"
          saveAction={{
            onAction: handleSave,
            loading: saveMutation.isPending,
          }}
          discardAction={{
            onAction: handleDiscard,
          }}
        />
      )}
      <Page
        title={pageTitle}
        backAction={{ onAction: () => navigate(`/e/${hcc}/venues${location.search}`) }}
        secondaryActions={
          !isNewVenue
            ? [
                {
                  content: t('venues.details.actions.delete'),
                  destructive: true,
                  onAction: () => setShowDeleteModal(true),
                },
              ]
            : []
        }
      >
        {Object.keys(fieldErrors).length > 0 && (
          <Box paddingBlockEnd="400">
            <Banner tone="critical" title={t("common.validationErrors")}>
              <BlockStack gap="200">
                {Object.values(fieldErrors).map((error, index) => (
                  <Text as="p" key={index}>
                    {error}
                  </Text>
                ))}
              </BlockStack>
            </Banner>
          </Box>
        )}

        <Layout>
          {/* Overview Card */}
          <Layout.Section>
            <OverviewCard
              venueData={venueData}
              fieldErrors={fieldErrors}
              hcc={hcc}
              onDataChange={handleDataChange}
            />
          </Layout.Section>

          {/* Location Card */}
          <Layout.Section>
            <LocationCard
              venueData={venueData}
              fieldErrors={fieldErrors}
              onDataChange={handleDataChange}
            />
          </Layout.Section>

          {/* Media Card */}
          <Layout.Section>
            <MediaCard
              venueData={venueData}
              isNewVenue={isNewVenue}
              isUploadingThumbnail={isUploadingThumbnail}
              hcc={hcc}
              venueId={venueId}
              onDataChange={handleDataChange}
              onRemoveMedia={handleRemoveMedia}
              onReorderMedia={handleReorderMedia}
              onDropMedia={handleDropMedia}
              onVideoThumbnailUpload={handleVideoThumbnailUpload}
            />
          </Layout.Section>
        </Layout>

        {/* Delete Confirmation Modal */}
        <Modal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title={t("venues.details.modal_delete.title")}
          primaryAction={{
            content: t("venues.details.modal_delete.cta"),
            onAction: handleDelete,
            destructive: true,
            loading: deleteMutation.isPending,
          }}
          secondaryActions={[
            {
              content: t("common.cancel"),
              onAction: () => setShowDeleteModal(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="200">
              <Text as="p" tone="subdued">
                {t("venues.details.modal_delete.content")}
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>
      </Page>
    </AppPageWrapper>
  );
}
